import React, { useEffect, useState } from 'react';
import { fetchFulfillmentDashboard } from './services/AdminStats.service';
import {
  faBan,
  faBook,
  faBookOpen,
  faCartShopping,
  faCircleCheck,
  faClock,
  faDollarSign,
  faHourglassHalf,
  faLocationDot,
  faRefresh,
  faTriangleExclamation,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrderFulfillmentStatusEnum } from '../../../interfaces/order.interface';
import { AdminNavHandler } from '../adminNav';
import AudienceStats from './components/AudienceStats';
import { Section, Tile } from './components/Tile';

const pctLabel = (v: number | null) => (v == null ? '—' : `${v}%`);

const AdminStats: React.FC<{ onOptionClick?: AdminNavHandler }> = ({ onOptionClick }) => {
  const [dashboard, setDashboard] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Bumped on MANUAL refresh only so AudienceStats, which owns its own fetch, reloads with us.
  // The mount fetch must not bump it — AudienceStats already fetches once on its own mount.
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchDashboard = async () => {
    setIsLoading(true);
    try {
      const dash = await fetchFulfillmentDashboard();
      if (dash) setDashboard(dash);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    fetchDashboard();
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openOrders = (chip?: string) => onOptionClick?.('All Orders', chip ? { ordersChip: chip } : undefined);
  // Deep-link with an exact status filter (no chip) — chip lists can merge extra orders (e.g.
  // needs_attention also pulls in stuck), so tiles that show a per-status count use this instead.
  const openOrdersByStatus = (status: string) => onOptionClick?.('All Orders', { ordersStatus: status });

  const pendingTotal = dashboard
    ? (Object.values(dashboard.funnel.pending) as number[]).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-storyblue text-lg font-bold">Dashboard</h2>
        <FontAwesomeIcon
          icon={faRefresh}
          className={`text-xl text-storyblue p-2 ${isLoading ? 'animate-spin cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          onClick={handleRefresh}
        />
      </div>

      {/* Audience first: registrations, classrooms, and signups lead the dashboard. */}
      <AudienceStats onOptionClick={onOptionClick} refreshKey={refreshKey} />

      {dashboard && (
        <>
          <Section title="Funnel (year to date)">
            <Tile label="Books started" value={dashboard.funnel.booksStarted} icon={faBookOpen} tone="blue" />
            <Tile
              label="Books finished"
              value={dashboard.funnel.booksFinished}
              icon={faBook}
              tone="green"
              sub={`${pctLabel(dashboard.conversion.startedToFinishedPct)} of started`}
            />
            <Tile
              label="Ordered"
              value={dashboard.funnel.ordered}
              icon={faCartShopping}
              tone="blue"
              sub={`${pctLabel(dashboard.conversion.finishedToOrderedPct)} of finished`}
              onClick={() => openOrders()}
            />
            <Tile
              label="Awaiting print"
              value={pendingTotal}
              icon={faHourglassHalf}
              tone="amber"
              sub={`${dashboard.funnel.pending.ready_to_dispatch ?? 0} ready to dispatch`}
              onClick={() => openOrders('ready_to_dispatch')}
            />
            <Tile
              label="At printer"
              value={dashboard.funnel.shipping.dispatched + dashboard.funnel.shipping.in_production}
              icon={faTruckFast}
              tone="blue"
              sub={`${dashboard.funnel.shipping.dispatched} in print jobs · ${dashboard.funnel.shipping.in_production} printing`}
            />
            <Tile
              label="Shipped"
              value={dashboard.funnel.shipped}
              icon={faCircleCheck}
              tone="green"
              sub={`${pctLabel(dashboard.conversion.orderedToShippedPct)} of ordered`}
              onClick={() => openOrders('shipped')}
            />
          </Section>

          <Section title="Last 30 days">
            {/* Revenue is per-currency (prices are stored in the charged currency; no FX rates). */}
            {(dashboard.last30d.revenue?.length ? dashboard.last30d.revenue : [null]).map((r: any) => (
              <Tile
                key={r?.currency || 'none'}
                label={`Revenue${r ? ` (${String(r.currency).toUpperCase()})` : ''}`}
                value={r ? Number(r.total).toLocaleString() : '—'}
                icon={faDollarSign}
                tone="green"
                sub={
                  r
                    ? `${r.orderCount} orders · avg ${r.avgOrderValue}${dashboard.last30d.revenueTruncated ? ' · truncated scan' : ''}`
                    : undefined
                }
              />
            ))}
            <Tile label="Orders" value={dashboard.last30d.ordered} icon={faCartShopping} tone="blue" />
            <Tile label="Books started" value={dashboard.last30d.booksStarted} icon={faBookOpen} tone="gray" />
            <Tile label="Books finished" value={dashboard.last30d.booksFinished} icon={faBook} tone="gray" />
            <Tile label="Shipped" value={dashboard.last30d.shipped} icon={faTruckFast} tone="green" onClick={() => openOrders('shipped')} />
          </Section>

          <Section title="Order health">
            <Tile
              label="Errors"
              value={dashboard.health.error}
              icon={faTriangleExclamation}
              tone="green"
              alert={dashboard.health.error > 0}
              onClick={() => openOrdersByStatus(OrderFulfillmentStatusEnum.ERROR)}
            />
            <Tile
              label="Missing address"
              value={dashboard.health.needsAddress}
              icon={faLocationDot}
              tone="green"
              alert={dashboard.health.needsAddress > 0}
              onClick={() => openOrdersByStatus(OrderFulfillmentStatusEnum.NEEDS_ADDRESS)}
            />
            <Tile
              label="Stuck"
              value={dashboard.health.stuck}
              icon={faClock}
              tone="green"
              alert={dashboard.health.stuck > 0}
              onClick={() => openOrders('stuck')}
            />
            <Tile label="Canceled" value={dashboard.health.canceled} icon={faBan} tone="gray" />
          </Section>
        </>
      )}

      {/* Outside the fulfillment block on purpose: the audience sections still render if the
          fulfillment dashboard fails to load, and vice versa. */}
    </div>
  );
};

export default AdminStats;
